import { useTheme } from "../ThemeContext";
import { useState, useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { fmt, DONE_STATUS } from "../theme";
import { KpiCard, DataTable, Card, EmptyState, ChartTooltip } from "../components/UI";
import { exportExcel, exportPDF, fmtMoeda, fmtData, getTurno, filtrarPorPeriodo } from "../utils/exportUtils";

const PERIODOS = [
  { key: "hoje", label: "Hoje" },
  { key: "ontem", label: "Ontem" },
  { key: "semana", label: "Semana" },
  { key: "mes", label: "Mes" },
  { key: "ano", label: "Ano" },
  { key: "custom", label: "Personalizado" },
];
const TURNOS = [
  { key: "Manha", label: "Manha", icon: "🌅", color: "#F59E0B" },
  { key: "Tarde", label: "Tarde", icon: "☀️", color: "#3B82F6" },
  { key: "Noite", label: "Noite", icon: "🌙", color: "#8B5CF6" },
];

export default function RelEntregadores({ orders, sub, lang }) {
  const { theme: C } = useTheme();
  const [periodo, setPeriodo] = useState("mes");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");

  const filtrados = useMemo(() => filtrarPorPeriodo(orders, periodo, dataInicio, dataFim), [orders, periodo, dataInicio, dataFim]);

  // ── Mapa de entregadores ──
  const porEnt = useMemo(() => {
    const m = {};
    filtrados.filter(o => o.entregador_id).forEach(o => {
      const id = o.entregador_id;
      if (!m[id]) m[id] = { id, nome: o.entregador_nome || `Entregador #${id}`, ped: 0, entregas: 0, fat: 0, taxa: 0, canc: 0, turno: { Manha: 0, Tarde: 0, Noite: 0 } };
      m[id].ped++;
      if (DONE_STATUS.includes(o.status)) {
        m[id].entregas++;
        m[id].fat += Number(o.total || 0);
        m[id].taxa += Number(o.taxa_entrega || 0);
        const t = getTurno(o.criado_em);
        if (m[id].turno[t] !== undefined) m[id].turno[t]++;
      }
      if (o.status === "cancelado") m[id].canc++;
    });
    return Object.values(m).sort((a, b) => b.entregas - a.entregas);
  }, [filtrados]);

  const totalEntregas = porEnt.reduce((s, e) => s + e.entregas, 0);
  const totalFat = porEnt.reduce((s, e) => s + e.fat, 0);
  const totalTaxa = porEnt.reduce((s, e) => s + e.taxa, 0);
  const mediaEnt = porEnt.length > 0 ? totalEntregas / porEnt.length : 0;

  // ── Por turno ──
  const turnoData = useMemo(() => {
    const m = { Manha: 0, Tarde: 0, Noite: 0 };
    filtrados.filter(o => o.entregador_id && DONE_STATUS.includes(o.status)).forEach(o => {
      const t = getTurno(o.criado_em);
      if (m[t] !== undefined) m[t]++;
    });
    return m;
  }, [filtrados]);

  const iS = { padding: "7px 12px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontSize: 12, outline: "none" };

  // ── Exportações ──
  const handleExportExcel = () => {
    const headers = ["Entregador", "ID", "Pedidos", "Entregas", "Faturamento", "Taxa Entrega", "Media/Entrega", "Cancelados", "Manha", "Tarde", "Noite"];
    const rows = porEnt.map(e => [
      e.nome, e.id, e.ped, e.entregas, e.fat.toFixed(2), e.taxa.toFixed(2),
      e.entregas > 0 ? (e.fat / e.entregas).toFixed(2) : "0.00", e.canc,
      e.turno.Manha, e.turno.Tarde, e.turno.Noite
    ]);
    rows.push(["TOTAL", "", porEnt.reduce((s, e) => s + e.ped, 0), totalEntregas, totalFat.toFixed(2), totalTaxa.toFixed(2), "", "", "", "", ""]);
    exportExcel(headers, rows, "relatorio_entregadores");
  };

  const handleExportPDF = () => {
    const kpiHTML = `<div class="kpi-row">
      <div class="kpi"><div class="kpi-val green">${totalEntregas}</div><div class="kpi-label">Total Entregas</div></div>
      <div class="kpi"><div class="kpi-val blue">${porEnt.length}</div><div class="kpi-label">Entregadores</div></div>
      <div class="kpi"><div class="kpi-val orange">${fmtMoeda(totalTaxa)}</div><div class="kpi-label">Taxas Entrega</div></div>
      <div class="kpi"><div class="kpi-val">${mediaEnt.toFixed(1)}</div><div class="kpi-label">Media/Entregador</div></div>
    </div>`;

    const turnoHTML = `<h2>Entregas por Turno</h2><table>
      <tr><th>Turno</th><th>Entregas</th><th>% do Total</th></tr>
      ${TURNOS.map(t => `<tr><td>${t.icon} ${t.label}</td><td>${turnoData[t.key]}</td><td>${totalEntregas > 0 ? (turnoData[t.key] / totalEntregas * 100).toFixed(1) : 0}%</td></tr>`).join("")}
    </table>`;

    const rankHTML = `<h2>Ranking de Entregadores</h2><table>
      <tr><th>#</th><th>Entregador</th><th>Entregas</th><th>Faturamento</th><th>Taxa</th><th>Media</th></tr>
      ${porEnt.map((e, i) => `<tr><td>${i + 1}</td><td>${e.nome}</td><td>${e.entregas}</td><td>${fmtMoeda(e.fat)}</td><td>${fmtMoeda(e.taxa)}</td><td>${fmtMoeda(e.entregas > 0 ? e.fat / e.entregas : 0)}</td></tr>`).join("")}
    </table>`;

    exportPDF("Relatorio de Entregadores", kpiHTML + turnoHTML + rankHTML, "relatorio_entregadores");
  };

  const FilterBar = () => (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 16, padding: 12, background: C.card, borderRadius: 12, border: `1px solid ${C.border}` }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, marginRight: 4 }}>Periodo:</span>
      {PERIODOS.map(p => (
        <button key={p.key} onClick={() => setPeriodo(p.key)}
          style={{ padding: "5px 12px", borderRadius: 8, border: `1px solid ${periodo === p.key ? C.orange : C.border}`,
            background: periodo === p.key ? C.orange + "18" : "transparent", color: periodo === p.key ? C.orange : C.textMuted,
            cursor: "pointer", fontSize: 11, fontWeight: 700 }}>
          {p.label}
        </button>
      ))}
      {periodo === "custom" && (
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} style={iS} />
          <span style={{ color: C.textMuted, fontSize: 11 }}>ate</span>
          <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} style={iS} />
        </div>
      )}
      <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
        <button onClick={handleExportExcel} style={{ padding: "5px 12px", borderRadius: 8, border: `1px solid ${C.green}40`, background: `${C.green}12`, color: C.green, cursor: "pointer", fontSize: 11, fontWeight: 700 }}>Excel</button>
        <button onClick={handleExportPDF} style={{ padding: "5px 12px", borderRadius: 8, border: `1px solid ${C.red}40`, background: `${C.red}12`, color: C.red, cursor: "pointer", fontSize: 11, fontWeight: 700 }}>PDF</button>
      </div>
    </div>
  );

  // ── Medalha de ranking ──
  const Medal = ({ pos }) => {
    const medals = { 1: "🥇", 2: "🥈", 3: "🥉" };
    return medals[pos] ? <span style={{ fontSize: 16 }}>{medals[pos]}</span> : <span style={{ fontSize: 12, fontWeight: 700, color: C.textMuted }}>{pos}</span>;
  };

  // ═══ SUB: GERAL ═══
  if (sub === "geral") {
    return (<>
      <FilterBar />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 16 }}>
        <KpiCard label="Total Entregas" value={totalEntregas} color={C.green} delay={0} />
        <KpiCard label="Entregadores Ativos" value={porEnt.length} color={C.blue} delay={.06} />
        <KpiCard label="Taxas de Entrega" value={fmt(totalTaxa)} color={C.orange} delay={.12} />
        <KpiCard label="Media / Entregador" value={mediaEnt.toFixed(1)} color={C.purple || "#8B5CF6"} delay={.18} />
      </div>

      {/* Turnos */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 16 }}>
        {TURNOS.map(t => (
          <div key={t.key} style={{ background: C.card, borderRadius: 12, padding: 14, border: `1px solid ${C.border}`, borderLeft: `4px solid ${t.color}` }}>
            <div style={{ fontSize: 10, color: C.textMuted, marginBottom: 4 }}>{t.icon} {t.label}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.text }}>{turnoData[t.key]}</div>
            <div style={{ fontSize: 9, color: C.textMuted }}>entregas ({totalEntregas > 0 ? (turnoData[t.key] / totalEntregas * 100).toFixed(0) : 0}%)</div>
          </div>
        ))}
      </div>

      {/* Ranking chart */}
      {porEnt.length > 0 && (
        <Card style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Ranking de Entregas</h3>
          <ResponsiveContainer width="100%" height={Math.max(180, Math.min(porEnt.length, 10) * 36)}>
            <BarChart data={porEnt.slice(0, 10).map(e => ({ nome: e.nome.length > 15 ? e.nome.slice(0, 15) + "..." : e.nome, entregas: e.entregas }))} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis type="number" tick={{ fill: C.textMuted, fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="nome" tick={{ fill: C.textSoft, fontSize: 10 }} axisLine={false} tickLine={false} width={120} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="entregas" radius={[0, 6, 6, 0]} fill={C.orange} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Tabela */}
      <Card>
        {porEnt.length === 0 ? <EmptyState text="Nenhuma entrega no periodo" /> : (<>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Detalhamento ({porEnt.length} entregadores)</h3>
          <DataTable headers={["#", "Entregador", "Entregas", "Faturamento", "Taxa Entrega", "Media", "Canc."]}>
            {porEnt.map((e, i) => (
              <tr key={e.id}>
                <td style={{ padding: "10px 12px", textAlign: "center" }}><Medal pos={i + 1} /></td>
                <td style={{ padding: "10px 12px", fontWeight: 700, fontSize: 13 }}>{e.nome}</td>
                <td style={{ padding: "10px 12px", fontWeight: 800, fontSize: 14 }}>{e.entregas}</td>
                <td style={{ padding: "10px 12px", fontWeight: 700, color: C.green }}>{fmt(e.fat)}</td>
                <td style={{ padding: "10px 12px", fontWeight: 700, color: C.orange }}>{fmt(e.taxa)}</td>
                <td style={{ padding: "10px 12px", color: C.textSoft }}>{fmt(e.entregas > 0 ? e.fat / e.entregas : 0)}</td>
                <td style={{ padding: "10px 12px", color: e.canc > 0 ? C.red : C.textMuted, fontWeight: e.canc > 0 ? 700 : 400 }}>{e.canc}</td>
              </tr>
            ))}
            {/* Totais */}
            <tr style={{ borderTop: `2px solid ${C.border}` }}>
              <td style={{ padding: "10px 12px" }} />
              <td style={{ padding: "10px 12px", fontWeight: 800, fontSize: 13 }}>TOTAL</td>
              <td style={{ padding: "10px 12px", fontWeight: 800, fontSize: 14 }}>{totalEntregas}</td>
              <td style={{ padding: "10px 12px", fontWeight: 800, color: C.green }}>{fmt(totalFat)}</td>
              <td style={{ padding: "10px 12px", fontWeight: 800, color: C.orange }}>{fmt(totalTaxa)}</td>
              <td style={{ padding: "10px 12px" }} />
              <td style={{ padding: "10px 12px" }} />
            </tr>
          </DataTable>
        </>)}
      </Card>
    </>);
  }

  // ═══ SUB: POR_ENTREGADOR ═══
  if (sub === "por_entregador") {
    return (<>
      <FilterBar />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12, marginBottom: 16 }}>
        <KpiCard label="Entregadores" value={porEnt.length} color={C.blue} delay={0} />
        <KpiCard label="Total Entregas" value={totalEntregas} color={C.green} delay={.06} />
      </div>

      {/* Cards individuais */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10, marginBottom: 16 }}>
        {porEnt.map((e, i) => (
          <div key={e.id} style={{ background: C.card, borderRadius: 14, padding: 16, border: `1px solid ${C.border}`, position: "relative" }}>
            <div style={{ position: "absolute", top: 10, right: 12 }}><Medal pos={i + 1} /></div>
            <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 8 }}>{e.nome}</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <div>
                <div style={{ fontSize: 9, color: C.textMuted }}>Entregas</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>{e.entregas}</div>
              </div>
              <div>
                <div style={{ fontSize: 9, color: C.textMuted }}>Faturamento</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.green }}>{fmt(e.fat)}</div>
              </div>
              <div>
                <div style={{ fontSize: 9, color: C.textMuted }}>Taxa Entrega</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.orange }}>{fmt(e.taxa)}</div>
              </div>
              <div>
                <div style={{ fontSize: 9, color: C.textMuted }}>Media</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.textSoft }}>{fmt(e.entregas > 0 ? e.fat / e.entregas : 0)}</div>
              </div>
            </div>
            {/* Turnos mini */}
            <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
              {TURNOS.map(t => (
                <div key={t.key} style={{ flex: 1, textAlign: "center", padding: "4px 0", borderRadius: 6, background: `${t.color}12`, border: `1px solid ${t.color}25` }}>
                  <div style={{ fontSize: 8, color: C.textMuted }}>{t.icon}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: t.color }}>{e.turno[t.key]}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>);
  }

  // ═══ SUB: A_RECEBER ═══
  if (sub === "a_receber") {
    return (<>
      <FilterBar />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 16 }}>
        <KpiCard label="Total a Pagar" value={fmt(totalTaxa)} color={C.orange} delay={0} />
        <KpiCard label="Entregadores" value={porEnt.filter(e => e.taxa > 0).length} color={C.blue} delay={.06} />
        <KpiCard label="Media / Entregador" value={fmt(porEnt.length > 0 ? totalTaxa / porEnt.length : 0)} color={C.green} delay={.12} />
      </div>
      <Card>
        {porEnt.filter(e => e.taxa > 0).length === 0 ? <EmptyState text="Nenhum valor pendente" /> : (<>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Valores a Pagar por Entregador</h3>
          <DataTable headers={["#", "Entregador", "Entregas", "Valor a Pagar"]}>
            {porEnt.filter(e => e.taxa > 0).map((e, i) => (
              <tr key={e.id}>
                <td style={{ padding: "10px 12px", textAlign: "center" }}><Medal pos={i + 1} /></td>
                <td style={{ padding: "10px 12px", fontWeight: 700, fontSize: 13 }}>{e.nome}</td>
                <td style={{ padding: "10px 12px", fontWeight: 700 }}>{e.entregas}</td>
                <td style={{ padding: "10px 12px", fontWeight: 800, fontSize: 15, color: C.orange }}>{fmt(e.taxa)}</td>
              </tr>
            ))}
            <tr style={{ borderTop: `2px solid ${C.border}` }}>
              <td style={{ padding: "10px 12px" }} />
              <td style={{ padding: "10px 12px", fontWeight: 800 }}>TOTAL</td>
              <td style={{ padding: "10px 12px", fontWeight: 800 }}>{totalEntregas}</td>
              <td style={{ padding: "10px 12px", fontWeight: 800, fontSize: 16, color: C.orange }}>{fmt(totalTaxa)}</td>
            </tr>
          </DataTable>
        </>)}
      </Card>
    </>);
  }

  // ═══ SUB: DIA / MES / ANO / PAGOS / SALDO (fallback) ═══
  return (<>
    <FilterBar />
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 16 }}>
      <KpiCard label="Total Entregas" value={totalEntregas} color={C.green} delay={0} />
      <KpiCard label="Entregadores" value={porEnt.length} color={C.blue} delay={.06} />
      <KpiCard label="Faturamento" value={fmt(totalFat)} color={C.orange} delay={.12} />
      <KpiCard label="Taxas Entrega" value={fmt(totalTaxa)} color={C.purple || "#8B5CF6"} delay={.18} />
    </div>
    <Card>
      {porEnt.length === 0 ? <EmptyState text="Nenhuma entrega no periodo" /> : (
        <DataTable headers={["#", "Entregador", "Entregas", "Faturamento", "Taxa", "Media"]}>
          {porEnt.map((e, i) => (
            <tr key={e.id}>
              <td style={{ padding: "10px 12px", textAlign: "center" }}><Medal pos={i + 1} /></td>
              <td style={{ padding: "10px 12px", fontWeight: 700 }}>{e.nome}</td>
              <td style={{ padding: "10px 12px", fontWeight: 800 }}>{e.entregas}</td>
              <td style={{ padding: "10px 12px", fontWeight: 700, color: C.green }}>{fmt(e.fat)}</td>
              <td style={{ padding: "10px 12px", fontWeight: 700, color: C.orange }}>{fmt(e.taxa)}</td>
              <td style={{ padding: "10px 12px", color: C.textSoft }}>{fmt(e.entregas > 0 ? e.fat / e.entregas : 0)}</td>
            </tr>
          ))}
        </DataTable>
      )}
    </Card>
  </>);
}
